import * as React from 'react';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Typography } from '@/components/common/typography';
import { Icon } from '@/components/common/icon';
import Icons from '@/const/icons';
import clsx from 'clsx';
import { ChevronDown } from 'lucide-react';

export interface DropdownFilterSortOption {
	label: string;
	value: string;
	iconNode: React.ReactNode;
}

export interface DropdownFilterSortProps {
	title: string;
	filterOptions: DropdownFilterSortOption[];
	sortOptions: DropdownFilterSortOption[];
	currentFilter: string;
	currentSort: string;
	onFilterChange: (val: string) => void;
	onSortChange: (val: string) => void;
	onClearAll: () => void;
}

export const DropdownFilterSort: React.FC<DropdownFilterSortProps> = ({
	title,
	filterOptions,
	sortOptions,
	currentFilter,
	currentSort,
	onFilterChange,
	onSortChange,
	onClearAll,
}) => {
	const [open, setOpen] = React.useState(false);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button className="relative min-w-[152px] flex items-center gap-x-2 p-2 text-black border border-smoke-50 rounded-lg justify-between md:justify-start disabled:cursor-not-allowed w-fit bg-white outline-none h-[33px]">
					<Typography
						type="semibold-14"
						className="line-clamp-1 flex-1 text-ellipsis overflow-hidden whitespace-normal text-left opacity-60"
					>
						{title}
					</Typography>
					<ChevronDown
						className={`size-5 shrink-0 text-black ${open && 'rotate-180'}`}
					/>
					{(currentFilter || currentSort) && (
						<span className="w-[10px] h-[10px] bg-main-blue rounded-full absolute -top-1 -right-1 z-20" />
					)}
				</button>
			</PopoverTrigger>
			<PopoverContent
				className="max-h-96 w-56 p-0 border-none z-10 overflow-y-auto scroll-custom shadow-shadow2"
				align="start"
			>
				<div className="flex flex-col bg-white">
					{/* Clear All */}
					<button
						className="flex items-center gap-x-3 px-4 py-3 hover:bg-grey-100 transition-colors w-full text-left bg-white text-black"
						onClick={onClearAll}
					>
						<Icon
							src={Icons.broom}
							width={18}
							height={18}
							className="text-primary"
						/>
						<Typography type="regular-14">Clear All</Typography>
					</button>

					{filterOptions.length > 0 && (
						<RadioGroup
							value={currentFilter}
							onValueChange={onFilterChange}
							className="gap-0 bg-white"
						>
							{filterOptions.map((option) => (
								<label
									key={option.value}
									className={clsx(
										'flex items-center justify-between px-4 py-3 hover:bg-grey-100 transition-colors cursor-pointer',
										{
											'bg-grey-100': currentFilter === option.value,
										}
									)}
								>
									<div className="flex items-center gap-x-3">
										{option.iconNode}
										<Typography type="regular-14">{option.label}</Typography>
									</div>
									<RadioGroupItem
										value={option.value}
										className="w-5 h-5 flex-shrink-0"
										id={`filter-${option.value}`}
									/>
								</label>
							))}
						</RadioGroup>
					)}

					{sortOptions.length > 0 && (
						<>
							<div className="h-px bg-slate-200 w-full" />
							<div className="px-4 py-2 bg-white">
								<Typography type="regular-14" className="text-opacity-black">
									sort by
								</Typography>
							</div>

							<RadioGroup
								value={currentSort}
								onValueChange={onSortChange}
								className="gap-0 bg-white"
							>
								{sortOptions.map((option) => (
									<label
										key={option.value}
										className={clsx(
											'flex items-center justify-between px-4 py-3 hover:bg-grey-100 transition-colors cursor-pointer',
											{
												'bg-grey-100': currentSort === option.value,
											}
										)}
									>
										<div className="flex items-center gap-x-3">
											{option.iconNode}
											<Typography type="regular-14">{option.label}</Typography>
										</div>
										<RadioGroupItem
											value={option.value}
											className="w-5 h-5 flex-shrink-0"
											id={`sort-${option.value}`}
											size="md"
										/>
									</label>
								))}
							</RadioGroup>
						</>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
};
